import {
  BackEndValueType,
  EAiActionStatus,
  EDatasourceType,
  ESdChangeReason,
  IActionDefinitionDto,
  IActionParameter,
  IAiActionInfo,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
import Form, { FormInstance } from "antd/es/form";
import jsonStringifySafe from "json-stringify-safe";
import ObjectHash from "object-hash";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { OpenAIService } from "../../../services/openai-service";
import SqlService from "../../../services/sql-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorTitle } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaPageHeader from "../../ui-kit/kuika-page-header/kuika-page-header";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSplitButton from "../../ui-kit/kuika-split-button/kuika-split-button";
import { AiActionPanel } from "./ai/ai-action-panel";
import { requestDatasourceReveal } from "./datasource-reveal";
import { AiActionSwitcherChip } from "./editor-switcher/ai-action-switcher-chip";
import "./editor-bar-surface.scss";

export interface ICreateAiActionState {
  /** Dims the page — for the save AND for generating the prompts from a description. */
  isSaveButtonDisabled: boolean;
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  submitButtonDisabledStatus: boolean;
  selectedTemplateId?: string;
  selectedTemplate?: any;
  isCreatingCustomTemplate: boolean;
  isEditingTemplate: boolean;
  actionName?: string;
  isNameOnEdit: boolean;
  model: string;
  returnType: "json" | "text";
  isCustomTemplateOnInit: boolean;
  isCreatingFromTemplate: boolean;
}

export interface ICreateAiActionProps {
  actionDatasource: IDatasourceDto;
  actionToEdit?: IActionDefinitionDto;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

class CreateAiAction extends PureSdComponent<ICreateAiActionProps & IWithSDProps, ICreateAiActionState> {
  formRef = React.createRef<FormInstance>();

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateAiActionProps & IWithSDProps) {
    super(props);
    this.state = {
      actionName: "",
      isSaveButtonDisabled: false,
      saveStatus: "idle",
      submitButtonDisabledStatus: true,
      selectedTemplateId: undefined,
      selectedTemplate: undefined,
      isCreatingCustomTemplate: false,
      isEditingTemplate: false,
      isNameOnEdit: false,
      model: "gpt-5",
      returnType: "text",
      isCustomTemplateOnInit: true,
      isCreatingFromTemplate: false
    };
  }

  componentDidUpdate(prevProps: ICreateAiActionProps & IWithSDProps) {
    if (prevProps.actionToEdit != this.props.actionToEdit && !this.props.actionToEdit) {
      this.handleLoadAction();
    } else if (
      this.props.actionToEdit &&
      this.props.actionToEdit !== undefined &&
      prevProps.actionToEdit != this.props.actionToEdit
    ) {
      this.handleLoadAction();
    }

    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleCloseDatasource();
    }
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidMount() {
    this.handleLoadAction();
  }

  generateDefaultActionName = (): string => {
    const baseName = "AI_Action";
    const existingNames = new Set(
      this.props.sd.actionDefinitions
        ?.filter((a) => a.actionType === EDatasourceType.AIActions)
        .map((a) => a.name?.toLocaleLowerCase()) ?? []
    );
    let counter = 1;
    while (existingNames.has(`${baseName}${counter}`.toLocaleLowerCase())) {
      counter++;
    }
    return `${baseName}${counter}`;
  };

  handleLoadAction = () => {
    const {
      actionToEdit,
      sd: { activeCreatingActionName }
    } = this.props;
    if (actionToEdit?.aiActionInfo) {
      const aiActionInfo = JSON.parse((actionToEdit.aiActionInfo as unknown as string) || "") as IAiActionInfo;
      const isTextReturnType = aiActionInfo.aiReturnType === "text" || !aiActionInfo.aiReturnType;

      this.props.sd.setAIActionDataForRightPanel(aiActionInfo, actionToEdit.actionParameters);

      this.setState(
        {
          actionName: actionToEdit.name,
          isSaveButtonDisabled: false,
          submitButtonDisabledStatus: true,
          selectedTemplateId: aiActionInfo.templateId,
          isCreatingCustomTemplate: true, // Always show editor when editing, regardless of template
          model: KMainFunctions.getOlderGeminiVersions().includes(aiActionInfo.model)
            ? KMainFunctions.getLastGeminiVersion()
            : aiActionInfo.model,
          returnType: isTextReturnType ? "text" : "json",
          isCustomTemplateOnInit: false
        },
        () => {
          // Set form values after setState so the custom template Form.Items are mounted
          requestAnimationFrame(() => {
            this.formRef.current?.setFieldsValue({
              name: actionToEdit.name,
              aiPrompt: aiActionInfo.aiPrompt,
              systemPrompt: aiActionInfo.systemPrompt,
              aiReturnType: isTextReturnType ? undefined : JSON.parse(aiActionInfo.aiReturnType || "{}")
            });
            this.objectHash = this.getObjectHash();
          });
        }
      );
    } else {
      const defaultName = this.generateDefaultActionName();
      this.formRef.current?.setFieldsValue({
        name: defaultName,
        aiPrompt: "",
        systemPrompt: ""
      });

      this.props.sd.setAIActionDataForRightPanel();

      this.setState(
        {
          actionName: defaultName,
          isSaveButtonDisabled: false,
          submitButtonDisabledStatus: true,
          model: "gpt-5",
          returnType: "text",
          isCustomTemplateOnInit: true
        },
        () => {
          this.objectHash = this.getObjectHash();
        }
      );
    }
  };

  setSubmitButtonDisabledStatus() {
    if (!this.state.selectedTemplateId && !this.state.isCreatingCustomTemplate) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }
    if (this.state.selectedTemplateId === "custom" && this.state.isCreatingCustomTemplate) {
      if (!this.formRef.current) {
        this.setState({ submitButtonDisabledStatus: true });
        return;
      }
      const formValues = this.formRef.current.getFieldsValue();

      if (!formValues.systemPrompt || !formValues.aiPrompt) {
        this.setState({ submitButtonDisabledStatus: true });
        return;
      }
    }

    if (this.objectHash === this.getObjectHash()) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }
    this.setState({ submitButtonDisabledStatus: false });
  }

  getObjectHash(): string {
    const { systemPrompt, aiPrompt, aiReturnType } = this.formRef.current?.getFieldsValue() || {};
    const { model, selectedTemplateId, returnType } = { ...this.state };
    const { stopSequences, temperature, max_length, top_p, frequency_penalty, presence_penalty } =
      this.props.sd.activeAIActionInfoForRightPanel || {};
    const actionName = this.formRef.current?.getFieldValue("name") || this.state.actionName;

    return ObjectHash({
      actionName,
      model,
      stopSequences,
      systemPrompt,
      aiPrompt,
      aiReturnType,
      returnType,
      actionParameters: this.props.sd.actionParameters,
      temperature,
      max_length,
      top_p,
      frequency_penalty,
      presence_penalty,
      selectedTemplateId
    });
  }

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
    this.props.sd.loadActionDefinitions();
  }

  handleCloseDatasource() {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  onImageSwitchChange = (newValue: any) => {
    let currentActionParameters = [...this.props.sd.actionParameters];
    const initialIndex = currentActionParameters.findIndex((element) => element.name === "GeminiFileParameter");

    if (newValue && initialIndex == -1) {
      const paramObj = {} as IActionParameter;
      paramObj.name = "GeminiFileParameter";
      paramObj.Typename = "String";
      currentActionParameters.push(paramObj);
    } else if (!newValue) {
      const index = currentActionParameters.findIndex((element) => element.name === "GeminiFileParameter");
      if (index !== -1) {
        currentActionParameters.splice(index, 1);
      }
    }
    this.props.sd.setAIActionDataForRightPanel(this.props.sd.activeAIActionInfoForRightPanel, currentActionParameters);
  };

  onAIPromptChange = (newValue: any, parameterTypes?: { parameterName: string; type: BackEndValueType }[]) => {
    const formFields = this.formRef.current?.getFieldsValue();

    // Use the newValue parameter for aiPrompt since the form field hasn't updated yet when onChange fires
    // Combine both prompts to extract all parameters
    const combinedText = `${formFields.systemPrompt || ""} ${newValue || ""}`;

    const paramsFromCode = SqlService.getSqlParameters(this.props.actionDatasource?.datasourceType, combinedText);

    const newParameters = [] as IActionParameter[];
    const currentActionParameters = [...this.props.sd.actionParameters];

    paramsFromCode?.forEach((paramFromCode) => {
      paramFromCode = paramFromCode.replace(/^@/, "").replace(/^:/, "");
      const curData = currentActionParameters.find((x) => x.name === paramFromCode);
      const paramObj = {} as IActionParameter;
      paramObj.name = paramFromCode;
      paramObj.Typename = curData
        ? curData.Typename
        : parameterTypes?.find((x) => x.parameterName === paramFromCode)?.type || "Unknown";
      newParameters.push(paramObj);
    });
    const GeminiFileParameter = currentActionParameters.find((x) => x.name === "GeminiFileParameter");
    if (GeminiFileParameter) {
      newParameters.push(GeminiFileParameter);
    }
    this.props.sd.setAIActionDataForRightPanel(this.props.sd.activeAIActionInfoForRightPanel, newParameters);
  };

  onTemplateChange = (value: string) => {
    let systemPrompt = "";
    let aiPrompt = "";
    let name = `AI_${value}`;

    switch (value) {
      case "grammarCorrector":
        systemPrompt = "Don't add any comments or any titles to the answer. Show only the corrected text.";
        aiPrompt = 'Can you correct the grammar of this text ""@text""';
        break;
      case "articleWriter":
        systemPrompt = "Don't stray from the target topic. Do not write subjective content.";
        aiPrompt = 'Can you write me an article about this topic? ""@topic""';
        break;
      case "examCreator":
        systemPrompt = "Add answers at the bottom. Don't add any comments. Show only the result.";
        aiPrompt = "Can you prepare a @questionNumber-question exam about @subject?";
        break;
      case "brandGenerator":
        systemPrompt = "The brands you recommend must be unique and have not been used before.";
        aiPrompt = "Can you suggest me unique brand names? Brief summary of my organization: @organizationBrief";
        break;
      case "personalTrainer":
        systemPrompt = "Do not recommend exercises that may cause health problems.";
        aiPrompt =
          "My gender: @gender, My age: @age, My height: @heightCm cm, my weight: @weightKg kg, I excersice @day days a week. My health problems: @healthProblems \n Can you suggest me a weekly exercise program?";
        break;
      case "personalDietExpert":
        systemPrompt = "Do not recommend any diet that may cause health problems.";
        aiPrompt =
          "My gender: @gender, My age: @age, My height: @heightCm cm, my weight: @weightKg kg, I excersice @day days a week. My health problems: @healthProblems \n Can you calculate how many calories I should consume daily? Can you suggest me a weekly diet program?";
        break;
      case "surveyCreator":
        systemPrompt = "Don't add any comments or any titles to the answer. Show only the questions.";
        aiPrompt = "Can you prepare a @questionNumber-question survey about @subject?";
        break;
      default:
        systemPrompt = "";
        aiPrompt = "";
        name = "Action1";
        break;
    }

    this.formRef.current?.setFieldsValue({
      systemPrompt,
      aiPrompt,
      name
    });

    const formFields = this.formRef.current?.getFieldsValue();

    value = `${value} ${formFields.aiPrompt} ${formFields.systemPrompt}`;

    const paramsFromCode = SqlService.getSqlParameters(this.props.actionDatasource?.datasourceType, value);

    const newParameters = [] as IActionParameter[];
    const currentActionParameters = [...this.props.sd.actionParameters];

    paramsFromCode?.forEach((paramFromCode) => {
      paramFromCode = paramFromCode.replace(/^@/, "").replace(/^:/, "");
      const curData = currentActionParameters.find((x) => x.name === paramFromCode);
      const paramObj = {} as IActionParameter;
      paramObj.name = paramFromCode;
      switch (paramObj.name) {
        case "text":
          paramObj.Typename = "String";
          break;
        case "topic":
          paramObj.Typename = "String";
          break;
        case "organizationBrief":
          paramObj.Typename = "String";
          break;
        case "questionNumber":
          paramObj.Typename = "Int32";
          break;
        case "subject":
          paramObj.Typename = "String";
          break;
        case "heightCm":
          paramObj.Typename = "Decimal";
          break;
        case "weightKg":
          paramObj.Typename = "Decimal";
          break;
        case "healthProblems":
          paramObj.Typename = "String";
          break;
        case "day":
          paramObj.Typename = "Int32";
          break;
        case "gender":
          paramObj.Typename = "String";
          break;
        case "age":
          paramObj.Typename = "Int32";
          break;
        default:
          paramObj.Typename = "Unknown";
          break;
      }
      newParameters.push(paramObj);
    });

    this.props.sd.setAIActionDataForRightPanel(this.props.sd.activeAIActionInfoForRightPanel, newParameters);
  };

  nameValidator = async (_rule, value: string) => {
    if (!value) throw new Error("Please enter an Action name");
    if (!/^[A-Za-z][A-Za-z0-9_]+$/.test(value)) throw new Error("Action name is invalid");

    // Preprod has legacy/orphaned rows where actionDatasource or action.name can
    // be null. `?.` makes the comparison return `undefined === "..."` → false
    // for those rows instead of crashing on .toLocaleLowerCase().
    const lowerValue = value.toLocaleLowerCase();

    if (this.props.actionDatasource?.name?.toLocaleLowerCase() === lowerValue) {
      throw new Error("Cannot be the same as Datasource name");
    }

    const isActionNameUsedBefore = this.props.sd.actionDefinitions.some(
      (action) =>
        !!action?.name &&
        action.kDatasource_Id === this.props.actionDatasource?.id &&
        action.name.toLocaleLowerCase() === lowerValue &&
        action.id !== this.props.actionToEdit?.id
    );

    if (isActionNameUsedBefore) throw new Error("This action name is used before");
  };

  isUnknownParameterExists = (parameters: IActionParameter[]) => {
    if (parameters == undefined) {
      return false;
    }
    const isUnknownExist = parameters.find((x) => x.Typename == "Unknown");

    if (isUnknownExist) {
      return false;
    }

    return true;
  };

  saveAction = async (data: any, status: EAiActionStatus = EAiActionStatus.Published) => {
    // The commit is live again as soon as the ✓ replaces the spinner, so a second press would
    // re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return undefined;
    try {
      this.saveStatus.start();
      this.setState({ isSaveButtonDisabled: true });
      const actionParameters = this.props.sd.actionParameters;

      if (!this.isUnknownParameterExists(actionParameters)) {
        KMainFunctions.displayWarningNotification("Please fill parameter types before save!!");
        this.saveStatus.reset();
        this.setState({
          isSaveButtonDisabled: false
        });
        return undefined;
      }

      let aiDatasource = this.props.sd.datasources?.find((ds) => ds.datasourceType === EDatasourceType.AIActions);

      if (!aiDatasource) {
        await DatasourceService.SaveDatasource({
          name: "AIActions",
          datasourceType: EDatasourceType.AIActions
        } as IDatasourceDto);

        await this.props.sd.refreshDatabases();

        aiDatasource = this.props.sd.datasources?.find((ds) => ds.datasourceType === EDatasourceType.AIActions);

        if (!aiDatasource) {
          throw new Error("AI Actions datasource could not be created. Please contact customer service.");
        }
      }

      const { aiPrompt, systemPrompt, aiReturnType } = data;
      const { model, returnType } = this.state;
      const actionName = data?.name || this.formRef.current?.getFieldValue("name") || this.state.actionName;
      const { stopSequences, temperature, max_length, top_p, frequency_penalty, presence_penalty } = {
        ...this.props.sd.activeAIActionInfoForRightPanel
      };

      const action = {
        id: this.props.actionToEdit?.id,
        name: actionName,
        kDatasource_Id: aiDatasource.id,
        kDatasource_Name: aiDatasource.name,
        actionType: EDatasourceType.AIActions,
        aiActionStatus: status,
        aiActionInfo: JSON.stringify({
          model,
          temperature,
          max_length,
          top_p,
          frequency_penalty,
          presence_penalty,
          stopSequences,
          aiPrompt,
          systemPrompt,
          aiReturnType: returnType === "json" ? jsonStringifySafe(aiReturnType) : "text"
        }) as unknown as IAiActionInfo,
        parameters: actionParameters
      } as IActionDefinitionDto;

      const res = await DatasourceService.SaveAction(action);

      if (res.status != 200) {
        // A resolved response that is not 200: the `finally` reset used to rescue this branch.
        this.saveStatus.fail();
        return undefined;
      }

      await this.props.sd.loadActionDefinitions();
      const resData = res.data;
      const addedAction = resData?.find((x) => x.name === action.name);
      const actionId = addedAction?.id;
      if (this.props.sd?.creatingDatasouceFolderId && actionId) {
        const folder = this.props.sd.getFolderById(
          this.props.sd?.creatingDatasouceFolderId,
          this.props.sd.project.actionFolders
        );
        if (folder && folder.parentFolderId) {
          this.props.sd.addActionToFolderInFolder(
            folder.parentFolderId,
            this.props.sd?.creatingDatasouceFolderId,
            actionId
          );
        } else {
          this.props.sd.addActionToFolder(this.props.sd?.creatingDatasouceFolderId, actionId);
        }
      }
      // The ✓ goes up first and the editor leaves after it — see `SaveStatus`.
      // A close confirmation this save answered ("Update & close") steps aside at once, so
      // the ✓ on the button is what is seen before the editor leaves.
      this.props.setShowModal(false);
      this.saveStatus.succeed((): void => {
        // The list behind the editor lights the row up; the button has already said "Saved".
        requestDatasourceReveal({ name: action.name, parentId: aiDatasource?.id });
        this.closeDataSource();
      });
    } catch (error) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
      return error;
    } finally {
      this.setState({ isSaveButtonDisabled: false });
    }
    return undefined;
  };

  setTemplateSelected = (modelId: string, name: string, template?: any) => {
    const dontChangeName: boolean =
      (this.state.selectedTemplateId === undefined || this.state.selectedTemplateId === "custom") &&
      modelId === "custom";

    this.setState({ selectedTemplateId: modelId, selectedTemplate: template }, () => {
      if (dontChangeName) return;
      const nextName = modelId !== "custom" ? this.generateUniqueTemplateActionName(name) : name;

      this.setState({ actionName: nextName, isNameOnEdit: false }, () => {
        this.formRef.current?.setFieldsValue({
          name: nextName
        });
      });
    });
  };

  private generateUniqueTemplateActionName = (baseName: string): string => {
    const normalizedBaseName = (baseName || "").trim();
    if (!normalizedBaseName) return baseName;

    const existingNames = new Set(
      (this.props.sd.actionDefinitions || [])
        .map((action) => (action.name || "").trim().toLowerCase())
        .filter((name) => !!name)
    );

    let index = 0;
    let candidate = normalizedBaseName;
    while (existingNames.has(candidate.toLowerCase())) {
      index += 1;
      candidate = `${normalizedBaseName}${index}`;
    }

    return candidate;
  };

  createFromTemplate = async (modelId: string, name: string, template: any) => {
    const finalName = this.generateUniqueTemplateActionName(name);

    this.setState(
      {
        selectedTemplateId: modelId,
        selectedTemplate: template,
        actionName: finalName,
        isNameOnEdit: false,
        isCreatingFromTemplate: true
      },
      async () => {
        this.formRef.current?.setFieldsValue({ name: finalName });
        await this.saveAiTemplate();
        this.setState({ isCreatingFromTemplate: false });
      }
    );
  };

  handleEditTemplate = async (templateIdOverride?: string) => {
    const selectedTemplateId = templateIdOverride ?? this.state.selectedTemplateId;
    if (!selectedTemplateId || selectedTemplateId === "custom") return;

    try {
      // Load template data and AI models from backend
      const [templateResponse, modelsResponse] = await Promise.all([
        DatasourceService.GetAiTemplates(),
        DatasourceService.GetAiModels()
      ]);

      const template = templateResponse.data.find((t) => t.id === selectedTemplateId);

      if (!template) {
        KMainFunctions.displayWarningNotification("Template not found");
        return;
      }

      // Find the model name from the model ID
      const aiModel = modelsResponse.data.find((m) => m.id === template.modelId);
      const modelName = aiModel?.name || "gpt-5";

      // Parse modelParameters to extract SystemMessage, UserMessage, and other data
      let modelParameters: any[] = [];
      try {
        // Check if modelParameters is already an array
        if (Array.isArray(template.modelParameters)) {
          modelParameters = template.modelParameters;
        } else if (typeof template.modelParameters === "string") {
          // The string contains literal newlines and other control characters that need to be escaped
          // Replace literal newlines, tabs, and carriage returns with their escaped versions
          let cleanedParams = template.modelParameters
            .replace(/\r\n/g, "\\n") // Windows line endings
            .replace(/\n/g, "\\n") // Unix line endings
            .replace(/\r/g, "\\r") // Old Mac line endings
            .replace(/\t/g, "\\t"); // Tabs

          modelParameters = JSON.parse(cleanedParams);
        } else {
          modelParameters = [];
        }
      } catch (e) {
        console.error("Failed to parse modelParameters:", e);
        console.error("Template modelParameters type:", typeof template.modelParameters);
        console.error("Template modelParameters value:", template.modelParameters);
        KMainFunctions.displayWarningNotification("Failed to parse template parameters. Check console for details.");
        return;
      }

      const systemMessageParam = modelParameters.find((p) => p.Name === "SystemMessage");
      const userMessageParam = modelParameters.find((p) => p.Name === "UserMessage");

      // Helper function to remove sentences containing "AiReturnTypeTemplate"
      const removeSentencesWithAiReturnTypeTemplate = (text: string): string => {
        if (!text) return text;

        // Split by common sentence delimiters but keep the delimiters
        const sentences = text.split(/([.!?]\s+)/);
        const filteredSentences: string[] = [];

        for (let i = 0; i < sentences.length; i++) {
          const sentence = sentences[i];
          // Check if this is actual content (not just delimiter)
          if (sentence.trim() && !sentence.match(/^[.!?]\s+$/)) {
            // Only add if it doesn't contain AiReturnTypeTemplate or {AiReturnTypeTemplate}
            if (!sentence.includes("AiReturnTypeTemplate") && !sentence.includes("{AiReturnTypeTemplate}")) {
              filteredSentences.push(sentence);
              // Add the delimiter if it exists
              if (i + 1 < sentences.length && sentences[i + 1].match(/^[.!?]\s+$/)) {
                filteredSentences.push(sentences[i + 1]);
                i++; // Skip the delimiter in next iteration
              }
            } else if (i + 1 < sentences.length && sentences[i + 1].match(/^[.!?]\s+$/)) {
              // Skip this sentence and its delimiter
              i++; // Skip the delimiter
            }
          }
        }

        return filteredSentences.join("").trim();
      };

      const systemPrompt = removeSentencesWithAiReturnTypeTemplate(systemMessageParam?.Value || "");
      const aiPrompt = removeSentencesWithAiReturnTypeTemplate(userMessageParam?.Value || "");

      // Use ExampleReturnDto first, fallback to AiReturnTypeTemplate for sample output data
      let templateReturnType: "json" | "text" = "text";
      let sampleOutputJson: string | undefined;

      if (template.returnType && template.returnType !== "text") {
        templateReturnType = "json";

        // Check for ExampleReturnDto first, then fallback to AiReturnTypeTemplate
        const sampleDataSource = template.exampleReturnDto || template.aiReturnTypeTemplate;

        if (sampleDataSource) {
          try {
            if (typeof sampleDataSource === "string") {
              // Clean up control characters and parse
              let cleanedTemplate = sampleDataSource
                .replace(/\r\n/g, "\\n")
                .replace(/\n/g, "\\n")
                .replace(/\r/g, "\\r")
                .replace(/\t/g, "\\t");

              // Try to parse and pretty-print it
              const parsed = JSON.parse(cleanedTemplate);
              sampleOutputJson = JSON.stringify(parsed, null, 2);
            } else if (typeof sampleDataSource === "object") {
              // Already an object, just stringify it
              sampleOutputJson = JSON.stringify(sampleDataSource, null, 2);
            }
          } catch (e) {
            console.error("Failed to parse sample data:", e);
            // Fallback to using the raw string if parsing fails
            sampleOutputJson =
              typeof sampleDataSource === "string" ? sampleDataSource : JSON.stringify(sampleDataSource, null, 2);
          }
        }
      }

      // Pre-fill form with template data including sample output JSON
      this.formRef.current?.setFieldsValue({
        name: this.state.actionName,
        systemPrompt,
        aiPrompt,
        aiReturnType: sampleOutputJson
      });

      // Extract temperature, maxTokens, etc. from modelParameters
      const temperatureParam = modelParameters.find((p) => p.Name === "Temperature");
      const maxTokensParam = modelParameters.find((p) => p.Name === "MaxTokens");
      const topPParam = modelParameters.find((p) => p.Name === "TopP");
      const frequencyPenaltyParam = modelParameters.find((p) => p.Name === "FrequencyPenalty");
      const presencePenaltyParam = modelParameters.find((p) => p.Name === "PresencePenalty");

      const aiActionInfo = {
        model: modelName,
        temperature: temperatureParam?.Value !== undefined ? parseFloat(temperatureParam.Value) : undefined,
        max_length: maxTokensParam?.Value !== undefined ? parseInt(maxTokensParam.Value) : undefined,
        top_p: topPParam?.Value !== undefined ? parseFloat(topPParam.Value) : undefined,
        frequency_penalty:
          frequencyPenaltyParam?.Value !== undefined ? parseFloat(frequencyPenaltyParam.Value) : undefined,
        presence_penalty:
          presencePenaltyParam?.Value !== undefined ? parseFloat(presencePenaltyParam.Value) : undefined,
        stopSequences: [],
        systemPrompt,
        aiPrompt
      } as IAiActionInfo;

      // Parse action parameters from modelParameters
      const newParameters = [] as IActionParameter[];
      modelParameters.forEach((param) => {
        // Skip system parameters
        if (
          [
            "SystemMessage",
            "UserMessage",
            "Temperature",
            "MaxTokens",
            "TopP",
            "FrequencyPenalty",
            "PresencePenalty",
            "Model",
            "Template"
          ].includes(param.Name)
        ) {
          return;
        }

        const paramObj = {
          name: param.Name,
          Typename: this.convertToBackendType(param.Typename)
        } as IActionParameter;
        newParameters.push(paramObj);
      });

      // Set AI action data for right panel with complete info
      this.props.sd.setAIActionDataForRightPanel(aiActionInfo, newParameters);

      // Update state to show custom template editor directly
      this.setState(
        {
          isCreatingCustomTemplate: true,
          isEditingTemplate: true,
          selectedTemplateId: "custom", // Change to custom so it saves through saveAction instead of saveAiTemplate
          model: modelName,
          returnType: templateReturnType,
          isCustomTemplateOnInit: false
        },
        () => {
          this.setSubmitButtonDisabledStatus();
        }
      );
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      KMainFunctions.displayWarningNotification("Failed to load template data");
    }
  };

  convertToBackendType = (typename: string): string => {
    // Convert template parameter types to backend types
    const typeMapping = {
      String: "String",
      Int32: "Int32",
      float: "Float",
      double: "Double",
      bool: "Boolean",
      Boolean: "Boolean"
    };
    return typeMapping[typename] || "String";
  };

  saveAiTemplate = async (actionId?: string) => {
    // The commit is live again as soon as the ✓ replaces the spinner, so a second press would
    // re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return undefined;
    this.saveStatus.start();
    try {
      let datasourceId: string | undefined = this.props.sd.datasources?.find(
        (ds) => ds.datasourceType === EDatasourceType.AIActions
      )?.id;

      if (!datasourceId) {
        await DatasourceService.SaveDatasource({
          name: "AIActions",
          datasourceType: EDatasourceType.AIActions
        } as IDatasourceDto);

        await this.props.sd.refreshDatabases();

        const newAiDatasource = this.props.sd.datasources?.find(
          (ds) => ds.datasourceType === EDatasourceType.AIActions
        );

        if (!newAiDatasource) {
          throw new Error("AI Actions datasource could not be created. Please contact customer service.");
        }

        datasourceId = newAiDatasource.id;
      }

      const res = await DatasourceService.SaveAiActionTemplate({
        datasourceId,
        aiTemplateId: this.state.selectedTemplateId ?? "",
        actionId: actionId ?? undefined,
        actionName: this.formRef.current?.getFieldValue("name")
      });
      if (res.status !== 200) {
        this.saveStatus.fail();
        return undefined;
      }

      await this.props.sd.loadActionDefinitions();

      const addedAction = this.props.sd.actionDefinitions?.find(
        (x) => x.name === this.formRef.current?.getFieldValue("name")
      );

      if (this.props.sd?.creatingDatasouceFolderId && addedAction?.id) {
        const folder = this.props.sd.getFolderById(
          this.props.sd?.creatingDatasouceFolderId,
          this.props.sd.project.actionFolders
        );
        if (folder && folder.parentFolderId) {
          this.props.sd.addActionToFolderInFolder(
            folder.parentFolderId,
            this.props.sd?.creatingDatasouceFolderId,
            addedAction?.id
          );
        } else {
          this.props.sd.addActionToFolder(this.props.sd?.creatingDatasouceFolderId, addedAction?.id);
        }
      }

      // The ✓ goes up first and the editor leaves after it — see `SaveStatus`.
      // A close confirmation this save answered ("Update & close") steps aside at once, so
      // the ✓ on the button is what is seen before the editor leaves.
      this.props.setShowModal(false);
      this.saveStatus.succeed((): void => {
        // The list behind the editor lights the row up; the button has already said "Saved".
        requestDatasourceReveal({ name: this.formRef.current?.getFieldValue("name") ?? "", parentId: datasourceId });
        this.closeDataSource();
      });
    } catch (error) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
      return error;
    }
    return undefined;
  };

  renderUpdateButton = () => (
    <KuikaSplitButton
      data-testid="aiActionUpdateButton"
      onClick={this.handlePublishSubmit}
      status={this.state.saveStatus}
      disabled={this.state.submitButtonDisabledStatus}
      menuLabel="More save actions"
      aria-label="Save actions"
      options={[
        {
          key: "save-draft",
          label: "Save as Draft",
          onClick: () => this.handleSaveAsDraftSubmit()
        }
      ]}
    >
      {/* Publishes, but labelled for what the user is doing: UPDATE on an action that
          already exists, SAVE while creating one — the wording every other tool's editor
          uses (Email Builder, Scheduled Jobs, Config Manager). */}
      {this.props.actionToEdit ? "UPDATE" : "SAVE"}
    </KuikaSplitButton>
  );

  /**
   * NEXT, or the UPDATE that commits a template. Only a commit carries `status`; its width then
   * follows the rolling label, so the fixed width the steps share is left off it.
   */
  renderSubmitButton = (label, onClick, status?: KuikaButtonStatus) => (
    <KuikaButton
      type="primary"
      disabled={this.state.submitButtonDisabledStatus}
      onClick={onClick}
      status={status}
      style={status === undefined ? { width: "5rem" } : undefined}
    >
      {label}
    </KuikaButton>
  );

  renderButtons = () => {
    const { actionToEdit } = this.props;
    const { isCreatingCustomTemplate, selectedTemplateId } = this.state;

    if (actionToEdit) {
      if (isCreatingCustomTemplate) {
        return this.renderUpdateButton();
      }
      return this.renderSubmitButton("UPDATE", () => this.saveAiTemplate(actionToEdit.id), this.state.saveStatus);
    }
    if (selectedTemplateId === "custom" && !isCreatingCustomTemplate) {
      return this.renderSubmitButton("NEXT", () => this.setState({ isCreatingCustomTemplate: true }));
    }
    if (isCreatingCustomTemplate) {
      return this.renderUpdateButton();
    }

    return null;
  };

  handleSubmitNameChange = () => {
    this.formRef.current
      ?.validateFields(["name"])
      .then(() => {
        this.setState({
          isNameOnEdit: false,
          actionName: this.formRef.current?.getFieldValue("name")
        });
      })
      .catch(() => {
        this.setState({ isNameOnEdit: true });
      });
  };

  handleFormValidationFailed = (errorInfo?: any) => {
    const errorFields = errorInfo?.errorFields || [];
    const firstError = errorFields[0];
    const firstMessage = firstError?.errors?.[0] || "Please fix the highlighted fields before creating the AI Action.";
    const firstFieldName = firstError?.name?.[0];

    if (firstFieldName === "name") {
      this.setState({ isNameOnEdit: true });
    }

    KMainFunctions.displayWarningNotification(firstMessage);
  };

  handleFormSubmit = async (status: EAiActionStatus = EAiActionStatus.Published) => {
    try {
      const data = await this.formRef.current?.validateFields();
      this.setState(
        {
          actionName: data?.name || this.state.actionName,
          isNameOnEdit: false
        },
        async () => {
          await this.saveAction(data, status);
        }
      );
    } catch (errorInfo) {
      this.handleFormValidationFailed(errorInfo);
    }
  };

  handlePublishSubmit = () => {
    this.handleFormSubmit(EAiActionStatus.Published);
  };

  handleSaveAsDraftSubmit = () => {
    this.handleFormSubmit(EAiActionStatus.Draft);
  };

  handleAbortNameChange = () => {
    if (!this.state.actionName) {
      KMainFunctions.displayWarningNotification("Please enter a valid action name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.actionName
    });
    this.setState({ isNameOnEdit: false });
  };

  handleNameInputOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    this.handleSubmitNameChange();
  };

  onReturnTypeChange = (returnType: "json" | "text") => {
    // if (returnType === "json") {
    //   const aiReturnType = this.formRef.current?.getFieldValue("aiReturnType");
    //   if (!aiReturnType?.length) {
    //     this.formRef.current?.setFieldsValue({
    //       ...this.formRef.current.getFieldsValue(),
    //       aiReturnType: [{ name: "", type: undefined, description: "" }]
    //     });
    //   }
    // }

    this.setState({ returnType });
  };

  onModelChange = (model: string) => {
    this.setState({ model }, () => {
      const isGemini1_5Flash001 = KMainFunctions.getFileUploadModels().includes(model);

      let currentActionParameters = [...this.props.sd.actionParameters];
      const initialIndex = currentActionParameters.findIndex((element) => element.name === "GeminiFileParameter");

      if (isGemini1_5Flash001 && initialIndex == -1) {
        const paramObj = {} as IActionParameter;
        paramObj.name = "GeminiFileParameter";
        paramObj.Typename = "String";
        currentActionParameters.push(paramObj);
      } else if (!isGemini1_5Flash001) {
        const index = currentActionParameters.findIndex((element) => element.name === "GeminiFileParameter");
        if (index !== -1) {
          currentActionParameters.splice(index, 1);
        }
      }
      this.props.sd.setAIActionDataForRightPanel(
        {
          ...(this.props.sd.activeAIActionInfoForRightPanel as IAiActionInfo),
          model
        },
        currentActionParameters
      );
    });
  };

  requestPromptsFromAI = async (value: string) => {
    try {
      this.setState({ isSaveButtonDisabled: true });
      const res = await OpenAIService.GenerateCustomTemplate(value);
      this.formRef.current?.setFieldsValue({
        ...this.formRef.current?.getFieldsValue(),
        systemPrompt: res.data.systemMessage,
        aiReturnType: res.data.responseFormat,
        aiPrompt: res.data.userMessage
      });
      this.props.sd.setAIActionDataForRightPanel(
        {
          temperature: 1,
          max_length: 576,
          top_p: 1,
          frequency_penalty: 0,
          presence_penalty: 0,
          model: res.data.fileUpload ? KMainFunctions.getLastGeminiVersion() : "gpt-5",
          aiPrompt: "",
          systemPrompt: "",
          stopSequences: []
        },
        this.props.sd.actionParameters
      );
      this.onAIPromptChange(res.data.userMessage, JSON.parse(res.data.parameters || "[]"));
      if (res.data.fileUpload) {
        this.onModelChange(KMainFunctions.getLastGeminiVersion());
      }
      this.setState({ isCustomTemplateOnInit: false, returnType: "json" });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isSaveButtonDisabled: false });
    }
  };

  render() {
    this.setSubmitButtonDisabledStatus();
    const actionButtons = this.renderButtons();
    const shouldShowActionHeader = this.state.isCreatingCustomTemplate;

    return (
      <Form
        ref={this.formRef}
        onFinish={this.saveAction}
        onFinishFailed={this.handleFormValidationFailed}
        onValuesChange={() => {
          this.setSubmitButtonDisabledStatus();
        }}
        layout="vertical"
        style={{ height: "100%", width: "100%" }}
      >
        <KuikaSpinnerSmall spinning={this.state.isSaveButtonDisabled}>
          <div className="kuika__data-diagram__create-con kuika__data-diagram__create-con--bar ai-action">
            {shouldShowActionHeader && (
              <KuikaPageHeader
                variant="bar"
                title={this.state.actionName ?? ""}
                onBack={() => this.handleCloseDatasource()}
                backLabel="Back to AI Functions"
                renderTitle={(): React.ReactNode => (
                  <KuikaEditorTitle
                    entityLabel="AI action"
                    isRenaming={this.state.isNameOnEdit}
                    // A record made from a template is named by the template, not the project.
                    canRename={
                      this.state.selectedTemplateId === "custom" || this.state.selectedTemplateId === undefined
                    }
                    canAbortRename={!!this.state.actionName}
                    onStartRename={() => this.setState({ isNameOnEdit: true })}
                    onConfirmRename={this.handleSubmitNameChange}
                    onAbortRename={this.handleAbortNameChange}
                    editTestId="aiActionNameEditButton"
                    confirmTestId="aiActionNameConfirmButton"
                    abortTestId="aiActionNameCancelButton"
                    identity={
                      <AiActionSwitcherChip
                        sd={this.props.sd}
                        setNextCallbackFunction={this.props.setNextCallbackFunction}
                        actionType={EDatasourceType.AIActions}
                        currentActionId={this.props.actionToEdit?.id}
                        currentName={this.state.actionName ?? ""}
                        triggerTestId="aiActionSwitcherTrigger"
                        onManageAll={() => this.handleCloseDatasource()}
                      />
                    }
                    renameField={
                      <Form.Item name={"name"} rules={[{ validator: this.nameValidator }]}>
                        <KuikaInput
                          onKeyDown={this.handleNameInputOnEnter}
                          placeholder="AI Action Name"
                          autoFocus
                          readOnly={
                            this.state.selectedTemplateId !== undefined && this.state.selectedTemplateId !== "custom"
                          }
                        />
                      </Form.Item>
                    }
                  />
                )}
                actions={actionButtons}
              />
            )}

            <div className="kuika__data-diagram__create-con__container ai-action__body-container">
              {/* <div className="kuika__data-diagram__create-con__settings-container">
                <span className="kuika__data-diagram__create-con__name-input-wrapper">
                  <Form.Item name="name" rules={[{ validator: this.nameValidator }]}>
                    <KuikaInput
                      placeholder="AI action name"
                      autoFocus
                      readOnly={
                        this.state.selectedTemplateId !== undefined && this.state.selectedTemplateId !== "custom"
                      }
                    />
                  </Form.Item>
                </span>
              </div> */}

              <div className="kuika__data-diagram__create-con__body ai-action-body">
                <AiActionPanel
                  isCustomTemplateOnInit={this.state.isCustomTemplateOnInit}
                  form={this.formRef}
                  isModal={false}
                  setTemplateSelected={this.setTemplateSelected}
                  selectedTemplateId={this.state.selectedTemplateId}
                  isCreatingCustomTemplate={this.state.isCreatingCustomTemplate}
                  isEditing={!!this.props.actionToEdit?.id}
                  isEditingTemplate={this.state.isEditingTemplate}
                  model={this.state.model}
                  returnType={this.state.returnType}
                  onTemplateChange={(value) => this.onTemplateChange(value)}
                  onAIPromptChange={(newValue) => this.onAIPromptChange(newValue)}
                  // actionParameters={this.state.actionParameters}
                  // updateParameters={(actionParameters) => {
                  //   this.setState({ actionParameters });
                  //   this.forceUpdate();
                  //   this.setSubmitButtonDisabledStatus();
                  // }}
                  onModelChange={this.onModelChange}
                  onReturnTypeChange={this.onReturnTypeChange}
                  requestPromptsFromAI={this.requestPromptsFromAI}
                  onClose={() => this.handleCloseDatasource()}
                  onEdit={(templateId) => this.handleEditTemplate(templateId)}
                  onCreateTemplate={this.createFromTemplate}
                  onStartBuilding={() => this.setState({ isCreatingCustomTemplate: true })}
                  isCreatingFromTemplate={this.state.isCreatingFromTemplate}
                />
              </div>
            </div>
            {actionButtons && !shouldShowActionHeader && (
              <div className="ai-action__footer">
                <KuikaButton type="text" onClick={() => this.handleCloseDatasource()}>
                  Cancel
                </KuikaButton>
                {actionButtons}
              </div>
            )}
          </div>
          <CloseConfirmationModal
            itemName={this.formRef.current?.getFieldValue("name")}
            closeType={"AI Action"}
            triggerModal={this.props.showModal}
            isLoading={this.state.isSaveButtonDisabled}
            updateAndCloseHandler={() => this.formRef.current?.submit()}
            closeWithoutSaveHandler={() => {
              this.props.setShowModal(false);
              this.closeDataSource();
            }}
            cancel={() => {
              this.props.setNextCallbackFunction();
              this.props.setShowModal(false);
            }}
          />
        </KuikaSpinnerSmall>
      </Form>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createAiAction = connect(mapStateToProps, mapDispatchToProps)(withSD<ICreateAiActionProps>(CreateAiAction));
export { createAiAction as CreateAiAction };
